import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { CpuIcon, PeakIcon } from '@utils/icons';
import type { IMonitorReplay, IReplayEvent } from '@interfaces/IMonitorApi';
import { dayScale } from './trackScale';

export type ReplayEventKind = IReplayEvent['kind'];

/** The rows of the lanes view, top to bottom; the peak has none and stays on the curve. */
export const REPLAY_LANES = ['safety', 'issues', 'activities', 'equipment'] as const;
export type ReplayLane = (typeof REPLAY_LANES)[number];
type ReplayEventOf<K extends ReplayEventKind> = Extract<IReplayEvent, { kind: K }>;

/** What a kind's copy needs: the translator, the day's equipment and the one floor-label rule. */
export interface IEventCopyContext {
  t: TFunction;
  equipment: IMonitorReplay['equipment'];
  floorLabel(floorId: string): string;
}

/** Everything the replay knows about one event kind. */
interface IEventKindSpec<E extends IReplayEvent> {
  color: string;
  glyph: ReactNode;
  /** Where markers of different kinds tie on time, the lower rank (more severe) goes first. */
  severity: number;
  /** The lanes view's row; null keeps the kind on the curve. */
  lane: ReplayLane | null;
  /** The floors an event happened on; none for the peak or an unlocated event. */
  floorIds(event: E): string[];
  /** The track marker's title. */
  title(event: E, context: IEventCopyContext): string;
  /** The building toast's title and sub line. */
  toast(event: E, context: IEventCopyContext): [title: string, sub: string];
}

/** A moved machine's copy: arrived on site, left it, or changed floor. A machine that left is placed on the floor it was last seen on. */
function equipmentMoveCopy(e: ReplayEventOf<'equipmentMoved'>, { t, equipment, floorLabel }: IEventCopyContext): [string, string] {
  const name = equipmentName(equipment, e.equipmentId);
  const { fromFloorId, toFloorId } = e;
  if (fromFloorId !== null && toFloorId !== null) {
    return [t('monitor.replay.markers.equipmentMoved', { name }), t('monitor.replay.toast.equipmentMoved', { from: floorLabel(fromFloorId), to: floorLabel(toFloorId) })];
  }
  const floor = locatedFloorIds(toFloorId ?? fromFloorId).map(floorLabel).join('');
  if (toFloorId === null) {
    return [t('monitor.replay.markers.equipmentLeft', { name }), t('monitor.replay.toast.equipmentLeft', { floor })];
  }
  return [t('monitor.replay.markers.equipmentArrived', { name }), t('monitor.replay.toast.equipmentArrived', { floor })];
}

const locatedFloorIds = (floorId: string | null) => (floorId === null ? [] : [floorId]);

/**
 * The replay's per-kind contract (spec, Timeline markers): each kind's colour,
 * glyph, severity, floors and copy, shared by the track's markers and the
 * building's pulses and toasts. Exhaustive by type: a new kind fails to compile
 * until it is described here. Nothing else is drawn.
 */
export const EVENT_KINDS: { [K in ReplayEventKind]: IEventKindSpec<ReplayEventOf<K>> } = {
  safety: {
    color: 'var(--red)',
    glyph: '!',
    severity: 0,
    lane: 'safety',
    floorIds: (e) => locatedFloorIds(e.floorId),
    title: (e, { t }) => safetyLabel(t, e.typeKey),
    toast: (e, { t }) => [safetyLabel(t, e.typeKey), t(e.isCritical ? 'monitor.replay.toast.safetyCritical' : 'monitor.replay.toast.safety')],
  },
  issueOpened: {
    color: 'var(--accent-attention)',
    glyph: '◆',
    severity: 1,
    lane: 'issues',
    floorIds: (e) => locatedFloorIds(e.floorId),
    title: (_e, { t }) => t('monitor.replay.markers.issueOpened'),
    toast: (_e, { t }) => [t('monitor.replay.markers.issueOpened'), t('monitor.replay.toast.issueOpened')],
  },
  activityAreaDone: {
    color: 'var(--accent-success)',
    glyph: '✓',
    severity: 2,
    lane: 'activities',
    floorIds: (e) => [e.floorId],
    title: (e, { t }) => t('monitor.replay.markers.activityAreaDone', { name: e.name }),
    toast: (e, { t }) => [t('monitor.replay.toast.activityAreaDone'), e.name],
  },
  activityCommitted: {
    color: 'var(--replay-marker-committed)',
    glyph: '▶',
    severity: 3,
    lane: 'activities',
    floorIds: (e) => e.floorIds,
    title: (e, { t }) => t('monitor.replay.markers.activityCommitted', { name: e.name }),
    toast: (e, { t }) => [t('monitor.replay.toast.activityCommitted'), e.name],
  },
  equipmentMoved: {
    color: 'var(--g500)',
    glyph: <CpuIcon size={11} />,
    severity: 4,
    lane: 'equipment',
    floorIds: (e) => locatedFloorIds(e.toFloorId ?? e.fromFloorId),
    title: (e, context) => equipmentMoveCopy(e, context)[0],
    toast: equipmentMoveCopy,
  },
  peak: {
    color: 'var(--replay-marker-peak)',
    glyph: <PeakIcon size={11} />,
    severity: 5,
    lane: null,
    floorIds: () => [],
    title: (e, { t }) => t('monitor.replay.markers.peak', { workers: e.workers, floors: e.floors }),
    toast: (e, { t }) => [t('monitor.replay.toast.peak', { workers: e.workers }), t('monitor.replay.toast.peakFloors', { floors: e.floors })],
  },
};

/** An event's kind spec, typed for any event (the record's entries are per-kind). */
export const kindSpecOf = <K extends ReplayEventKind>(event: ReplayEventOf<K>): IEventKindSpec<ReplayEventOf<K>> => EVENT_KINDS[event.kind];

/** An equipment id's name for the day; an id missing from the dictionary reads as itself. */
export const equipmentName = (equipment: IMonitorReplay['equipment'], id: string) => equipment[id]?.name ?? id;

/** A safety event's violation label; a type the app has no label for reads as a generic safety event. */
export const safetyLabel = (t: TFunction, typeKey: string) =>
  t(`monitor.safetyViolationType.${typeKey}`, { defaultValue: t('monitor.replay.markers.safety') });

/** An event placed on the track. */
export interface IPlacedEvent {
  event: IReplayEvent;
  ms: number;
}

/**
 * The day's drawable events in time order, inside the window. `events` already
 * holds the derived ones (equipment moves, the peak); a kind a newer backend
 * sends that this app can't draw is skipped.
 */
export function placeEvents(replay: IMonitorReplay, start: number, end: number): IPlacedEvent[] {
  return replay.events
    .filter((event) => event.kind in EVENT_KINDS)
    .map((event) => ({ event, ms: Date.parse(event.at) }))
    .filter(({ ms }) => ms >= start && ms <= end)
    .sort((a, b) => a.ms - b.ms);
}

/** Where a placed event is drawn in the lanes view: its kind's lane, or the curve for the peak. */
export type MarkerRowKey = ReplayLane | 'curve';

/** The day's markers, placed once: every one in time order, and the same split by lane. */
export interface IDayMarkers {
  all: IPlacedEvent[];
  byLane: Record<MarkerRowKey, IPlacedEvent[]>;
}

/** Places the day's events inside its window and splits them by lane, for the track and its legend alike. */
export function dayMarkers(replay: IMonitorReplay): IDayMarkers {
  const { start, end } = dayScale(replay);
  const all = placeEvents(replay, start, end);
  const byLane: IDayMarkers['byLane'] = { curve: [], safety: [], issues: [], activities: [], equipment: [] };
  for (const placed of all) byLane[EVENT_KINDS[placed.event.kind].lane ?? 'curve'].push(placed);
  return { all, byLane };
}

/** A lane's legend colours: one per kind it holds, in the kind table's order. */
export const laneColors = (lane: ReplayLane): string[] =>
  Object.values(EVENT_KINDS).flatMap((spec) => (spec.lane === lane ? [spec.color] : []));

/** Track markers of one kind merged because they sit too close to tell apart. */
export interface IMarkerCluster {
  events: IPlacedEvent[];
  /** The first event's x, in px. */
  x: number;
  /** Where the marker is drawn, in px: `x`, kept inside the plot's ends. */
  drawX: number;
  /** The kind of every event in `events`. */
  kind: ReplayEventKind;
}

/**
 * Merges time-sorted events of one kind into clusters: an event closer to its
 * cluster's first event than `reachPx(size)` — the cluster's size once it joins
 * — joins it. A wider (more digits) marker so reaches further and never overlaps the next.
 */
function clusterKind(
  placed: IPlacedEvent[],
  xOf: (ms: number) => number,
  reachPx: (size: number) => number,
): IMarkerCluster[] {
  const out: IMarkerCluster[] = [];
  for (const p of placed) {
    const x = xOf(p.ms);
    const last = out.at(-1);
    if (last && x - last.x < reachPx(last.events.length + 1)) last.events.push(p);
    else out.push({ events: [p], x, drawX: x, kind: p.event.kind });
  }
  return out;
}

/** A kind's stacking layer inside its marker row: the more severe, the higher, so overlapping markers show the worst on top. */
export const paintLayer = (kind: ReplayEventKind) => Object.keys(EVENT_KINDS).length - 1 - EVENT_KINDS[kind].severity;

/**
 * Clusters each kind separately — kinds never merge — and draws every marker
 * at its own time: markers of different kinds that meet overlap rather than
 * being pushed apart, since a nudged marker would claim a time (even one past
 * the playhead) its events never had; the lanes view is for crowded days. Only
 * the plot's ends move a marker, so none hangs off the track. Returned in time
 * order (the more severe kind first on a tie); where markers overlap,
 * `paintLayer` puts the more severe on top. `markerPx(size)` is a marker's
 * rendered width.
 */
export function clusterMarkers(
  placed: IPlacedEvent[],
  xOf: (ms: number) => number,
  reachPx: (size: number) => number,
  markerPx: (size: number) => number,
  plotPx: number,
): IMarkerCluster[] {
  const clusters = [...new Set(placed.map((p) => p.event.kind))]
    .flatMap((kind) => clusterKind(placed.filter((p) => p.event.kind === kind), xOf, reachPx))
    .sort((a, b) => a.x - b.x || EVENT_KINDS[a.kind].severity - EVENT_KINDS[b.kind].severity);
  for (const c of clusters) {
    const half = markerPx(c.events.length) / 2;
    c.drawX = Math.min(Math.max(c.x, half), plotPx - half);
  }
  return clusters;
}
